import { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';

/**
 * Calls cognitoUser.getSession() to get a current token. The cognitoUser.getSession() method
 * does the work of refreshing the token when appropriate.
 */
export const getSession = (
    cognitoUser: CognitoUser,
): Promise<CognitoSessionResult> =>
    new Promise((resolve, reject) => {
        // cognitoUser.getSession() will first see whether the tokens in local storage exist and are
        // still valid; if not, it will try to use whatever refreshToken it finds there to
        // establish a new session. See: https://stackoverflow.com/a/46627024/5565964
        cognitoUser.getSession(
            (err: Error, session: CognitoUserSession): void => {
                if (err) {
                    // If there is an error, it means the session could not be refreshed and the
                    // user needs to manually re-authenticate.
                    reject(err);
                } else {
                    const sessionIsValid = session.isValid();
                    // We used to use session.getAccessToken() to use the accessToken for auth,
                    // but now we're using the idToken. Both work from a client perspective; the
                    // backend uses some additional information (userId and email) encoded in the
                    // idToken to implement additional security logic.
                    const tokenInstance = session.getIdToken();
                    const token = tokenInstance.getJwtToken();
                    const expiration = tokenInstance.getExpiration();
                    const issuedAt = tokenInstance.getIssuedAt();

                    resolve({
                        token,
                        expiration,
                        issuedAt,
                        sessionIsValid,
                    });
                }
            },
        );
    });
